import { Routes, Route, useLocation, Navigate, useNavigate } from 'react-router-dom';
import { lazy, Suspense, useEffect, useRef } from 'react';
import { Dialog, Toast } from 'antd-mobile';
import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { AddOutline } from 'antd-mobile-icons';
import { useAuthStore } from './stores/authStore';
import Login from './pages/Login';
import { dispatchBackHandler } from './hooks/useBackHandler';
import useHapticFeedback from './hooks/useHapticFeedback';
import { NavGlyph } from './components/Icon';


const Home = lazy(() => import('./pages/Home'));
const Customers = lazy(() => import('./pages/Customers'));
const CustomerDetail = lazy(() => import('./pages/CustomerDetail'));
const CustomerForm = lazy(() => import('./pages/CustomerForm'));
const SaleForm = lazy(() => import('./pages/SaleForm'));
const Search = lazy(() => import('./pages/Search'));
const Settings = lazy(() => import('./pages/Settings'));
const Statistics = lazy(() => import('./pages/Statistics'));
const CategoryManage = lazy(() => import('./pages/CategoryManage'));
const PriceLevelManage = lazy(() => import('./pages/PriceLevelManage'));

type TabKind = 'home' | 'customers' | 'record' | 'search' | 'profile';

const tabs: Array<{ key: string; title: string; kind: TabKind }> = [
  { key: '/', title: '首页', kind: 'home' },
  { key: '/customers', title: '客户', kind: 'customers' },
  { key: 'record', title: '记一笔', kind: 'record' },
  { key: '/search', title: '搜索', kind: 'search' },
  { key: '/settings', title: '我的', kind: 'profile' }
];

const tabPaths = ['/', '/customers', '/search', '/settings'];

function secondaryDestination(pathname: string, search: string) {
  const params = new URLSearchParams(search);
  const source = params.get('from');
  const customerId = params.get('customerId');
  const returnTo = params.get('returnTo');
  const statisticsFrom = params.get('statisticsFrom') === 'settings' ? 'settings' : 'home';

  if (returnTo?.startsWith('/')) return returnTo;
  if (source === 'search') return '/search';
  if (source === 'statistics') return `/statistics?from=${statisticsFrom}`;
  if (source === 'customers') {
    const listParams = new URLSearchParams();
    const village = params.get('village');
    const listFrom = params.get('listFrom');
    if (village) listParams.set('village', village);
    if (listFrom === 'search' || listFrom === 'statistics') listParams.set('from', listFrom);
    if (listFrom === 'statistics') listParams.set('statisticsFrom', statisticsFrom);
    const query = listParams.toString();
    return `/customers${query ? `?${query}` : ''}`;
  }
  if (source === 'customer' && customerId) return `/customer/${customerId}`;
  if (pathname === '/statistics') return source === 'settings' ? '/settings' : '/';
  if (/^\/settings\/categories\/[^/]+\/price-levels$/.test(pathname)) {
    return '/settings/categories';
  }
  if (pathname.startsWith('/customer/')) return '/customers';
  if (pathname.startsWith('/settings/')) return '/settings';
  return '/';
}

function IOSBottomTab() {
  const location = useLocation();
  const navigate = useNavigate();

  return (
    <div className="wine-tabbar">
      {tabs.map((tab) => {
        const isRecord = tab.key === 'record';
        const isActive = location.pathname === tab.key;
        return (
          <button
            key={tab.key}
            type="button"
            aria-label={tab.title}
            aria-current={isActive ? 'page' : undefined}
            onClick={() => isRecord ? navigate('/sale') : navigate(tab.key, { replace: true })}
            className={`wine-tabbar__item${isActive ? ' wine-tabbar__item--active' : ''}${isRecord ? ' wine-tabbar__item--record' : ''}`}
            data-haptic={isRecord ? 'medium' : 'selection'}
          >
            <span className="wine-tabbar__icon">
              {isRecord ? <AddOutline /> : <NavGlyph kind={tab.kind as Exclude<TabKind, 'record'>} active={isActive} />}
            </span>
            <span>{tab.title}</span>
          </button>
        );
      })}
    </div>
  );
}

function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const { token } = useAuthStore();
  const lastExitBackRef = useRef(0);

  useHapticFeedback();
  useEffect(() => {
    const viewport = window.visualViewport;
    const update = () => {
      document.documentElement.style.setProperty('--wine-visible-height', `${viewport?.height || window.innerHeight}px`);
      document.documentElement.style.setProperty('--wine-viewport-top', `${viewport?.offsetTop || 0}px`);
    };
    update(); viewport?.addEventListener('resize', update); viewport?.addEventListener('scroll', update);
    return () => { viewport?.removeEventListener('resize', update); viewport?.removeEventListener('scroll', update); };
  }, []);


  useEffect(() => {
    lastExitBackRef.current = 0;
    document.querySelector('.wine-app-scroll')?.scrollTo({ top: 0 });
  }, [location.pathname]);

  useEffect(() => {
    const handleBack = async () => {
      if (document.querySelector('.adm-dialog')) {
        const cancel = document.querySelector<HTMLButtonElement>('.adm-dialog-footer .adm-dialog-button');
        if (cancel) cancel.click(); else Dialog.clear();
        return;
      }

      if (await dispatchBackHandler()) return;

      const isExitPage = location.pathname === '/' || location.pathname === '/login';
      if (isExitPage) {
        const now = Date.now();
        if (now - lastExitBackRef.current <= 2000) {
          CapacitorApp.exitApp();
        } else {
          lastExitBackRef.current = now;
          Toast.show({ content: '再按一次退出应用' });
        }
        return;
      }

      const hasExplicitSource = new URLSearchParams(location.search).has('from');
      if (tabPaths.includes(location.pathname) && !hasExplicitSource) {
        navigate('/', { replace: true });
        return;
      }

      navigate(secondaryDestination(location.pathname, location.search), { replace: true });
    };

    const isNative = Capacitor.isNativePlatform();
    const listener = isNative
      ? CapacitorApp.addListener('backButton', handleBack)
      : null;

    return () => {
      listener?.then((handle) => handle.remove());
    };
  }, [location.pathname, location.search, navigate]);

  const isLoginPage = location.pathname === '/login';
  if (!token && !isLoginPage) {
    return <Navigate to="/login" replace />;
  }
  if (token && isLoginPage) {
    return <Navigate to="/" replace />;
  }

  const isTabPage = tabPaths.includes(location.pathname);
  if (isLoginPage) return <div className="wine-app-shell ios-app-no-tab"><div className="wine-app-scroll"><Login /></div></div>;

  return (
    <div
      className={`wine-app-shell${isTabPage ? ' wine-app-shell--tab' : ' ios-app-no-tab'}`}
    >
      <div className="ios-scroll-view wine-app-scroll">
        <div className="wine-route" key={location.pathname}>
            <Suspense fallback={<div className="ios-page" style={{ display: 'grid', placeItems: 'center' }}>加载中...</div>}>
              <Routes location={location}>
              <Route path="/" element={<Home />} />
              <Route path="/customers" element={<Customers />} />
              <Route path="/customer/:id" element={<CustomerDetail />} />
              <Route path="/customer/form" element={<CustomerForm />} />
              <Route path="/customer/form/:id" element={<CustomerForm />} />
              <Route path="/sale" element={<SaleForm />} />
              <Route path="/sale/:id/edit" element={<SaleForm />} />
              <Route path="/search" element={<Search />} />
              <Route path="/statistics" element={<Statistics />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/settings/categories" element={<CategoryManage />} />
              <Route path="/settings/categories/:categoryId/price-levels" element={<PriceLevelManage />} />
              <Route path="/login" element={<Login />} />
              <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
        </div>
      </div>
      {isTabPage && <IOSBottomTab />}
    </div>
  );
}

export default App;
